import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	concat,
	debounceTime, delay,
	filter,
	firstValueFrom,
	map, merge,
	Observable,
	of,
	ReplaySubject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';
import { GdIcons } from 'src/app/ui/app-gd.icons';

import { DxListsStorage, IDxList } from '../../../core/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { AppPublicProjectView } from '../../../ui/components/app-public-project-view/app-public-project-view.entity';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	constructEntityList,
	EntitySelector,
	fetchPublicProjectDetails,
	navigateToCreateFromEntity,
	navigateToEntity,
	navigateToEntityCreate,
	navigateToEntityDuplicate,
	openCreateDatablocksConfirmationPopup,
	openDeleteConfirmationPopup,
	openPersistConfirmationPopup,
	openRepersistConfirmationPopup,
	openUnpersistConfirmationPopup,
	refreshEntityList,
	stopPersistence,
	toggleEntityStatus, updateEntitiesFrameStatus,
	updateIncludePublic,
} from '../../store';
import {
	EntityItemEntity,
	ParentProject,
	PersistenceState,
} from './entity-item.entity';
import { EntityListEntity } from './entity-list.entity';
import {OptionChangedEvent} from "devextreme/ui/accordion";

type OptionChangeAction = 'page-change' | 'pageSize-change' | 'sort' | 'filter';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

type EntityOverlayDetails = {
	detailsEntityId: number | string | undefined;
	persistenceEntityId: number | undefined;
};

@Component({
	selector: 'app-entity-list',
	templateUrl: './entity-list.component.html',
	styleUrls: ['./entity-list.component.scss'],
	inputs: ['projectLabel'],
})
export class EntityListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@entity.list.action.duplicate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.duplicateEntity(entity.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.toggleActivationStatus(entity);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@entity.list.action.duplicate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.duplicateEntity(entity.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const entity = item as EntityItemEntity;
				this.toggleActivationStatus(entity);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public isImportedHeaderFilter = [
		{
			text: $localize`:i18n=@@entity.list.column.imported:`,
			value: ['isImported', '=', true],
		},
		{
			text: $localize`:i18n=@@entity.list.column.unimported:`,
			value: ['isImported', '=', false],
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public projectHeaderFilter: Array<DxHeaderFilterItem> = [];
	public isPersistedHeaderFilter = [
		{
			text: $localize`:i18n=@@entity.list.column.persistance.persisted:`,
			value: true,
		},
		{
			text: $localize`:i18n=@@entity.list.column.persistance.unpersisted:`,
			value: false,
		},
	];

	public vo$: Observable<{
		isLoading: boolean;
		isFirstLoad: boolean;
		noDataText: string;
		isPublicProjectDetailsLoading: boolean;
		list: EntityListEntity;
		includePublic: boolean;
		openedOverlays: EntityOverlayDetails;
		publicProjectDetails: AppPublicProjectView | null;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly entityListSubject = new ReplaySubject<EntityListEntity>(1);

	private readonly loadTriggerSubject = new BehaviorSubject<boolean>(true);

	private readonly openedOverlaySubject =
		new BehaviorSubject<EntityOverlayDetails>({
			persistenceEntityId: undefined,
			detailsEntityId: undefined,
		});

	private readonly optionChangedSubject = new ReplaySubject<OptionChangeAction>(1);
	private readonly refreshSubject = new ReplaySubject<void>(1);

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	public constructor(
		private readonly store: Store,
		private readonly entitySelector: EntitySelector,
		private readonly dxStorage: DxListsStorage,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'entity-list';
		this.dxLocalStorageKey = DcDxGrids.EntityListStandalone;

		const targetEntity = this.openedOverlaySubject.pipe(
			filter((val) => val.detailsEntityId !== undefined),
			switchMap((val) =>
				this.entitySelector.getEntity$(val.detailsEntityId as number)
			),
			map((e) => this.buildPublicProjectDetails(e))
		);
		this.vo$ = combineAll({
			isLoading: this.entitySelector.getLoading$(),
			noDataText: this.entitySelector
				.getLoading$()
				.pipe(
					map((v) =>
						v
							? $localize`:i18n=@@entity.list.dataLoading:`
							: $localize`:i18n=@@entity.list.noData:`
					)
				),
			isFirstLoad: this.loadTriggerSubject,
			isPublicProjectDetailsLoading:
				this.entitySelector.isPublicProjectDetailsLoading$(),
			list: this.entityListSubject,
			includePublic: this.entitySelector.includePublic$(),
			openedOverlays: this.openedOverlaySubject,
			publicProjectDetails: concat(of(null), targetEntity),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(constructEntityList());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@entity.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.entitySelector
			.getEntityList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				filter((l) => l.availableColumns.size !== 0),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.projectHeaderFilter = l
						.extractAvailableProjects()
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.entityListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();

		// Triggers loading after importing project for the first time
		this.entityListSubject
			.pipe(
				takeUntil(this.optionChangedSubject),
				switchMap((list) =>
					combineAll({
						list,
						count: timer(2000),
					})
				),
				tap(({ list }) => {
					const firstEl = list.elements.first();
					if (firstEl?.persistenceState.isDefault) {
						this.refreshSubject.next();
					}
				})
			)
			.subscribe();

		merge(
			this.optionChangedSubject,
			this.refreshSubject.pipe(
				delay(550),
				map(() => 'refresh')
			)
		)
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(520),
				filter(() => this.grid !== null),
				tap((val) => {
					const items = this.grid?.instance
						?.getDataSource()
						?.items() as Array<EntityItemEntity>;
					const entitesIds = items?.map((it) => it.id) ?? [];
					this.store.dispatch(
						updateEntitiesFrameStatus({
							ids: entitesIds,
							shouldReload: val === 'refresh',
						})
					);
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.loadTriggerSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshEntityList());
		this.refreshSubject.next();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public onOptionChange(e: OptionChangedEvent): void {
		if (!this.grid) {
			return;
		}
		const action: string = e.fullName;
		if (action === 'paging.pageIndex') {
			this.optionChangedSubject.next('page-change');
			return;
		}

		if (action === 'paging.pageSize') {
			this.optionChangedSubject.next('pageSize-change');
			return;
		}

		if (action.includes('filterValue')) {
			this.optionChangedSubject.next('filter');
			return;
		}

		if (action.includes('sortOrder')) {
			this.optionChangedSubject.next('sort');
			return;
		}
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.loadTriggerSubject);
		const isLoadingInProgress = await firstValueFrom(
			this.entitySelector.getLoading$()
		);

		if (firstLoad && !isLoadingInProgress) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.loadTriggerSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public createNewEntity(): void {
		this.store.dispatch(navigateToEntityCreate());
	}

	public stopPersistence(event: Event, entity: EntityItemEntity): void {
		event.stopPropagation();
		if (entity.persistenceState.recentJobId === null) {
			return;
		}
		this.store.dispatch(
			stopPersistence({
				jobKey: entity.persistenceState.recentJobId,
			})
		);
	}

	public async openPersistencePopover(
		event: Event,
		entity: EntityItemEntity
	): Promise<void> {
		event.stopPropagation();
		const previous = await firstValueFrom(this.openedOverlaySubject);
		this.openedOverlaySubject.next({
			persistenceEntityId:
				previous.persistenceEntityId === entity.id ? undefined : entity.id,
			detailsEntityId: previous.detailsEntityId,
		});
	}

	public closePersistencePopover(): void {
		this.openedOverlaySubject.next({
			persistenceEntityId: undefined,
			detailsEntityId: undefined,
		});
	}

	public persistEntity(entity: EntityItemEntity): void {
		this.store.dispatch(
			openPersistConfirmationPopup({
				entityId: [entity.id],
				entityLabel: entity.label,
			})
		);
	}

	public depersist(entity: EntityItemEntity): void {
		this.store.dispatch(
			openUnpersistConfirmationPopup({
				entityId: [entity.id],
				entityLabel: entity.label,
			})
		);
	}

	public rePersist(entity: EntityItemEntity): void {
		this.store.dispatch(
			openRepersistConfirmationPopup({
				entityId: entity.id,
				entityLabel: entity.label,
			})
		);
	}

	public async navigateToEntity(e: EntityItemEntity): Promise<void> {
		if (!e.parentProject.isInSameProject && e.parentProject.isPublic) {
			const previous = await firstValueFrom(this.openedOverlaySubject);
			this.openedOverlaySubject.next({
				detailsEntityId: previous.detailsEntityId === e.id ? undefined : e.id,
				persistenceEntityId: previous.persistenceEntityId,
			});
			this.store.dispatch(
				fetchPublicProjectDetails({
					entityId: e.id,
				})
			);
			return;
		}
		this.store.dispatch(
			navigateToEntity({
				id: e.id,
			})
		);
	}

	public exportEntity(id: number): void {}

	public deleteEntity(event: Event, entity: EntityItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			openDeleteConfirmationPopup({
				entityId: [entity.id],
				entityLabel: entity.label,
			})
		);
	}

	public createDBFromEntity(event: Event, id: number): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToCreateFromEntity({
				id,
			})
		);
	}

	public duplicateEntity(id: number, event?: Event): void {
		if (event) {
			event.stopPropagation();
		}
		this.store.dispatch(
			navigateToEntityDuplicate({
				id,
			})
		);
	}

	public toggleIncludePublic(value: MatSelectChange): void {
		this.store.dispatch(
			updateIncludePublic({
				includePublic: value.value,
			})
		);
	}

	private async createDatablocksFromSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		return this.store.dispatch(
			openCreateDatablocksConfirmationPopup({
				entityId: selectedEntities.map((e) => e.id),
			})
		);
	}

	private async persistSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		return this.store.dispatch(
			openPersistConfirmationPopup({
				entityId: selectedEntities.map((e) => e.id),
				entityLabel:
					selectedEntities.length === 1 ? selectedEntities[0].label : '',
			})
		);
	}
	private async unpersistSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		return this.store.dispatch(
			openUnpersistConfirmationPopup({
				entityId: selectedEntities.map((e) => e.id),
				entityLabel:
					selectedEntities.length === 1 ? selectedEntities[0].label : '',
			})
		);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedEntities = await firstValueFrom(
			this.entitySelector.getSelectedEntities$(selectedRowKeys)
		);
		if (selectedEntities.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					entityId: [selectedEntities[0].id],
					entityLabel: selectedEntities[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				entityId: selectedEntities.map((k) => k.id),
				entityLabel: '',
			})
		);
	}

	public toggleActivationStatus(entity: EntityItemEntity): void {
		this.store.dispatch(
			toggleEntityStatus({
				entityId: entity.id,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculatePersistenceFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as PersistenceState;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { isPersisted } = entity;
			return isPersisted === !!value;
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateProjectFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as ParentProject;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { label } = entity;
			return !!label && checkStringEquality(label, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: EntityItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public async openPublicProjectDetailsPopover(
		event: Event,
		entityId: number
	): Promise<void> {
		event.stopPropagation();
		const previous = await firstValueFrom(this.openedOverlaySubject);
		if (previous.detailsEntityId === entityId) {
			this.openedOverlaySubject.next({
				...previous,
				detailsEntityId: undefined,
			});
			return;
		}
		this.openedOverlaySubject.next({
			...previous,
			detailsEntityId: entityId,
		});
		this.store.dispatch(
			fetchPublicProjectDetails({
				entityId,
			})
		);
	}

	public async closeOverlay(): Promise<void> {
		const previous = await firstValueFrom(this.openedOverlaySubject);
		this.openedOverlaySubject.next({
			persistenceEntityId: previous.persistenceEntityId,
			detailsEntityId: undefined,
		});
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.MoveDatablock,
			ActionIcons.PersistInDisk,
			ActionIcons.ExposeDatablock,
			ActionIcons.UnPersist,
			ActionIcons.CreateFromDatablock,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.createDatablocksFromSelected(),
				iconName: actionMap.get(ActionIcons.CreateFromDatablock)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.createDBFromEntity:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.persistSelected(),
				iconName: actionMap.get(ActionIcons.PersistInDisk)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.diskPersistence:`,
				typed: 'plain-context-action',
			},
			{
				order: 3,
				command: (): Promise<void> => this.unpersistSelected(),
				iconName: actionMap.get(ActionIcons.UnPersist)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.action.depersistAll:`,
				typed: 'plain-context-action',
			},
			{
				order: 4,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@entity.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}

	private buildPublicProjectDetails(
		e: EntityItemEntity | undefined
	): AppPublicProjectView {
		if (!e) {
			return AppPublicProjectView.build();
		}
		return AppPublicProjectView.build({
			characCount: e.parentProject.charCount,
			depotCount: e.parentProject.depotCount,
			projectLabel: e.parentProject.label,
			pictoUrl: e.parentProject.pictoUrl,
			elemLabel: e.label,
			elemCode: e.code,
			elemId: e.id,
			createdOn: e.creationDate,
			createdBy: e.createdByAvatar,
			updatedBy: e.updatedByAvatar,
			updatedOn: e.updateDate,
		});
	}
}
